import React from 'react';
import {
	PropertyDefinitionBelongsTo,
	PropertyDefinitionHas,
	BelongsToPopulatedValue,
} from '../types';
import type { PropertyDefinition } from '../types';
import {
	Box,
	Theme,
	Typography,
	useMediaQuery,
} from '@mui/material';
import {entryPropertyGetReadableValue, entryPropertyGetSecondaryValue, extractPropertyValueUrls} from '../common/util';
import { Link } from 'react-router-dom';
import { ExternalLinkOverlay } from './ExternalLinkOverlay';

/** Props for scalar properties (string, boolean, text, select, etc.). */
export interface EntryPropertyScalarProps {
	propertyDefinition: Exclude<PropertyDefinition, PropertyDefinitionBelongsTo | PropertyDefinitionHas>;
	value: string;
}

/** Props for relation properties (BelongsTo / Has). */
export interface EntryPropertyRelationProps {
	propertyDefinition: PropertyDefinitionBelongsTo | PropertyDefinitionHas;
	values: BelongsToPopulatedValue[];
	onNavigateToEntry: (entryId: string) => void;
	onDisconnectEntry?: (id: string) => void;
}

interface ScalarBlockProps {
	def: EntryPropertyScalarProps['propertyDefinition'];
	value: string;
	onEditClick?: () => void;
	disabled?: boolean;
}

interface RelationBlockProps {
	disableTabIndex?: boolean;
	def: PropertyDefinitionBelongsTo | PropertyDefinitionHas;
	values: Pick<BelongsToPopulatedValue, 'value' | 'label'>[];
	onDialogOpen: () => void;
	disabled?: boolean;
}

export const EntryPropertyTextBlock = ({def, value, onEditClick, disabled}: ScalarBlockProps) => { 
	const readableValue = entryPropertyGetReadableValue(def, value);
	const secondaryValue = entryPropertyGetSecondaryValue(def, value);
	const isSmScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
	const urls = Array.from(new Set(extractPropertyValueUrls(def, value)));
	return (
		<Box
			key={def.slug}
			onClick={onEditClick}
			sx={{position: 'relative', '&:hover': disabled ? undefined : {cursor: 'pointer', backgroundColor: isSmScreen ? undefined : 'action.hover'}, py: 1, px: 2, mt: -1}}
		>
			<Typography variant='caption' color='grey' sx={{mt: 1}}>{def.label}</Typography>
			<Box display='flex' flexDirection='column'>
				{readableValue ? <Typography variant='body1' sx={{color: 'text.primary'}}>
					{readableValue}
				</Typography> : <Typography variant='body1' sx={{color: 'grey'}}>-</Typography>}
				{secondaryValue && <Typography variant='caption' color='primary.light'>
					{secondaryValue}
				</Typography>}
			</Box>
			{urls.length > 0 && <ExternalLinkOverlay urls={urls} />}
		</Box>
	);
};

export const EntryPropertyMultilineTextBlock = ({def, value, onEditClick, disabled}: ScalarBlockProps) => { 
	const readableValue = entryPropertyGetReadableValue(def, value);
	const secondaryValue = entryPropertyGetSecondaryValue(def, value);
	const isSmScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
	const urls = Array.from(new Set(extractPropertyValueUrls(def, value)));
	return (
		<Box
			key={def.slug}
			onClick={onEditClick}
			sx={{position: 'relative', '&:hover': disabled ? undefined : {cursor: 'pointer', backgroundColor: isSmScreen ? undefined : 'action.hover'}, py: 1, px: 2, mt: -1}}
		>
			<Typography variant='caption' sx={{color: 'grey', mt: 1}}>{def.label}</Typography>
			<Box display='flex' flexDirection='column'>
				{readableValue ? <Typography variant='body1' sx={{whiteSpace: 'pre-wrap'}}>
					{readableValue}
				</Typography> : <Typography variant='body1' sx={{color: 'grey', whiteSpace: 'pre-wrap'}}>-</Typography>}
				{secondaryValue && <Typography variant='caption' color='text.secondary' sx={{whiteSpace: 'pre-wrap'}}>
					{secondaryValue}
				</Typography>}
			</Box>
			{urls.length > 0 && <ExternalLinkOverlay urls={urls} />}
		</Box>
	);
};

export const EntryPropertyBelongsToBlock = ({disableTabIndex, def, values, onDialogOpen, disabled}: RelationBlockProps) => {
	const isSmScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
	return (
		<Box display='flex' flexDirection='column' sx={{'&:hover':  disabled ? undefined : { cursor: 'pointer', backgroundColor: isSmScreen ? undefined : 'action.hover' }, py: 1, px: 2, mt: -1}} onClick={disabled ? undefined : onDialogOpen}>
			<Box display='flex' alignItems='center'>
				<Typography variant='caption' sx={{color: 'grey', mt: 1}}>
					{def.label}
				</Typography>
			</Box>
			<Box sx={{display: 'flex', gap: 0.5, flexWrap: 'wrap'}} >
				<Link
					to='#'
					tabIndex={disableTabIndex ? -1 : undefined}
					onClick={(e) => {
						e.preventDefault();
						if (!disabled) onDialogOpen();
					}}
					style={{textDecoration: 'none'}}
				>
					{values.length > 0 ? <Typography variant='body1'>
						{entryPropertyGetReadableValue(def, values)}
					</Typography> : <Typography variant='body1' sx={{color: 'grey'}}>-</Typography>}
				</Link>
			</Box>
		</Box>
	);
};
